import { Component, OnInit, OnDestroy, AfterViewInit, inject, PLATFORM_ID, ChangeDetectorRef, HostListener, ViewChild, ElementRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Observable, of, catchError, throwError, Subscription, filter, Subject, merge } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SearchComponent } from '../search/search.component';
import { environment } from '../../../environments/environment';
import { SearchService, SearchRequest, SearchResponse, UserSearchCriteria } from '../../services/search.service';
import { toLocalISOString } from '../../core/utils/date.util';
import { BlogService } from '../../services/blog.service';
import { PopularService, PopularItem } from '../../services/popular.service';
import { Blog } from '../../core/models/blog.model';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { DateRangePickerComponent } from '../../shared/components/date-range-picker/date-range-picker.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { NewsletterComponent } from '../../shared/components/newsletter/newsletter.component';
import { FeaturesComponent } from "../../shared/components";

// Swiper global olarak vendors.js içinden yüklenir
declare var Swiper: any;

enum LocationType {
  City = 0,
  Country = 1,
  Continent = 2,
  District = 3,
  Area = 4,
  Hotel = 5,
  Category = 6
}

interface AutocompleteResult {
  id: number;
  label: string;
  value: number;
  parentId: string;
  type: string;
  masterHotelId?: number; // Kıbrıs otelleri için MasterHotelId
}

interface AutocompleteResponse {
  results: AutocompleteResult[];
  errors: any[];
  total: number;
  types: { type: string; count: number; }[];
}


@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    SearchComponent,
    RouterLink,
    DateRangePickerComponent,
    LoadingComponent,
    NewsletterComponent,
    FeaturesComponent
]
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  private readonly apiUrl = environment.apiUrl;
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);
  
  
  searchForm: FormGroup;
  searchResults: SearchResponse | null = null;
  private dateSubscription?: Subscription;
  private routerSubscription?: Subscription;
  loading = false;
  error: string | null = null;
  
  selectedLocation: AutocompleteResult | null = null;
  
  @ViewChild('autoTrigger', { read: MatAutocompleteTrigger }) autoTrigger!: MatAutocompleteTrigger;
  @ViewChild('popularTrack') popularTrack!: ElementRef;
  activePopularIndex = 0;
  activePromoSlide = 0;
  private promoTimer: any = null;
  bentoOffset = 0;
  private bentoRotateTimer: any = null;
  mobileCarouselSlides: { type: 'destination' | 'promo'; item?: any; promoId?: string }[] = [];

  isCalendarOpen = false;
  isGuestSelectorOpen = false;
  isMenuActive = false;
  isMobileLocationOpen = false;
  mobileLocationSearch = '';
  minDate = new Date();

  // Daktilo placeholder
  typingPlaceholder = '';
  private typingTexts = ['Antalya Otelleri', 'Bodrum Otelleri', 'Kıbrıs Otelleri', 'Ege Otelleri', 'Alanya Otelleri'];
  private typingIndex = 0;
  private typingCharIndex = 0;
  private typingForward = true;
  private typingTimer: any = null;

  // Mobil takvim overlay
  mobileCalendarTab: 'calendar' | 'holidays' | 'weekends' = 'calendar';

  holidays: { name: string; start: Date; end: Date; icon: string; singleDay: boolean }[] = [];
  upcomingWeekends: { label: string; start: Date; end: Date }[] = [];

  getNightCount(start: Date, end: Date): number {
    return Math.round((end.getTime() - start.getTime()) / 86400000);
  }

  private buildHolidays(): void {
    // 2026 resmi tatil günleri (sadece resmi tatil, haftasonu birleşmesi otomatik hesaplanır)
    const rawHolidays = [
      { name: 'Ramazan Bayramı', days: [new Date(2026, 2, 20), new Date(2026, 2, 21), new Date(2026, 2, 22)], icon: '🌙' },
      { name: '23 Nisan Ulusal Egemenlik ve Çocuk Bayramı', days: [new Date(2026, 3, 23)], icon: '🇹🇷' },
      { name: '1 Mayıs Emek ve Dayanışma Günü', days: [new Date(2026, 4, 1)], icon: '🇹🇷' },
      { name: '19 Mayıs Gençlik ve Spor Bayramı', days: [new Date(2026, 4, 19)], icon: '🇹🇷' },
      { name: 'Kurban Bayramı', days: [new Date(2026, 4, 27), new Date(2026, 4, 28), new Date(2026, 4, 29), new Date(2026, 4, 30)], icon: '🐑' },
      { name: '15 Temmuz Demokrasi Bayramı', days: [new Date(2026, 6, 15)], icon: '🇹🇷' },
      { name: '30 Ağustos Zafer Bayramı', days: [new Date(2026, 7, 30)], icon: '🇹🇷' },
      { name: '29 Ekim Cumhuriyet Bayramı', days: [new Date(2026, 9, 29)], icon: '🇹🇷' },
      { name: 'Yılbaşı Tatili', days: [new Date(2027, 0, 1)], icon: '🎄' },
    ];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.holidays = rawHolidays
      .map(h => {
        // Tatil günlerinin başı ve sonunu bul
        let start = new Date(h.days[0]);
        let end = new Date(h.days[h.days.length - 1]);

        // Öncesinde haftasonu varsa birleştir (Cumartesi=6, Pazar=0)
        while (true) {
          const prev = new Date(start);
          prev.setDate(prev.getDate() - 1);
          if (prev.getDay() === 0 || prev.getDay() === 6) {
            start = prev;
          } else {
            break;
          }
        }

        // Sonrasında haftasonu varsa birleştir
        while (true) {
          const next = new Date(end);
          next.setDate(next.getDate() + 1);
          if (next.getDay() === 0 || next.getDay() === 6) {
            end = next;
          } else {
            break;
          }
        }

        // Çıkış tarihi = son günün ertesi
        const checkout = new Date(end);
        checkout.setDate(checkout.getDate() + 1);

        return { name: h.name, start, end: checkout, icon: h.icon, singleDay: h.days.length === 1 };
      })
      .filter(h => h.end >= today);
  }

  private buildWeekends(): void {
    const weekends: { label: string; start: Date; end: Date }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(today);
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

    while (weekends.length < 8) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() === 6) { // Cumartesi
        const sat = new Date(d);
        const sun = new Date(d);
        sun.setDate(sun.getDate() + 1);
        weekends.push({
          label: `${sat.getDate()} - ${sun.getDate()} ${months[sun.getMonth()]}`,
          start: sat,
          end: sun
        });
      }
    }
    this.upcomingWeekends = weekends;
  }

  selectHoliday(h: { start: Date; end: Date }): void {
    this.onDateRangeSelected({ startDate: h.start, endDate: h.end });
  }

  selectWeekend(w: { start: Date; end: Date }): void {
    this.onDateRangeSelected({ startDate: w.start, endDate: w.end });
  }

  filteredLocations$: Observable<AutocompleteResult[]>;
  isLocationFocused = false;
  locationFocus$ = new Subject<string>();

  quickSuggestions: AutocompleteResult[] = [];

  searchParams: any = {};
  childrenAges: number[] = []; // 1. oda çocuk yaşları
  room2ChildrenAges: number[] = []; // 2. oda çocuk yaşları
  activeAgeDropdown: number | null = null; // Aktif yaş dropdown'u
  blogs: Blog[] = [];
  blogLoading = false;

  private startTypingEffect(): void {
    const tick = () => {
      const text = this.typingTexts[this.typingIndex];
      if (this.typingForward) {
        this.typingCharIndex++;
        this.typingPlaceholder = text.substring(0, this.typingCharIndex);
        if (this.typingCharIndex >= text.length) {
          this.typingForward = false;
          this.typingTimer = setTimeout(tick, 1500);
          return;
        }
        this.typingTimer = setTimeout(tick, 80);
      } else {
        this.typingCharIndex--;
        this.typingPlaceholder = text.substring(0, this.typingCharIndex);
        if (this.typingCharIndex <= 0) {
          this.typingForward = true;
          this.typingIndex = (this.typingIndex + 1) % this.typingTexts.length;
          this.typingTimer = setTimeout(tick, 400);
          return;
        }
        this.typingTimer = setTimeout(tick, 40);
      }
    };
    this.typingTimer = setTimeout(tick, 600);
  }

  private stopTypingEffect(): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
  }

  // ⭐ Popüler Öğeler - API'den dinamik yüklenir
  popularItems: PopularItem[] = [];
  popularLoading = false;

  constructor(
    private searchService: SearchService, 
    private blogService: BlogService,
    public popularService: PopularService
  ) {
    // Constructor'da cache varsa hemen göster
    const cached = this.popularService.cachedItems;
    if (cached && cached.length > 0) {
      this.popularItems = [...cached];
      this.activePopularIndex = Math.floor(cached.length / 2);
      this.buildQuickSuggestions();
      this.popularLoading = false;
    }
    this.searchForm = this.fb.group({
      location: ['', Validators.required],
      checkInDate: [null],
      checkOutDate: [null],
      adults: [2, [Validators.required, Validators.min(1), Validators.max(10)]],
      children: [0, [Validators.required, Validators.min(0), Validators.max(6)]],
      rooms: [1, [Validators.required, Validators.min(1), Validators.max(2)]],
      // 2. oda için alanlar (oda 2 seçilirse kullanılır)
      room2Adults: [1, [Validators.min(1), Validators.max(10)]],
      room2Children: [0, [Validators.min(0), Validators.max(6)]],
      maxPrice: [5000],
      minPrice: [500]
    });

    // Başlangıçta çocuk sayısına göre yaş array'ini initialize et
    const initialChildrenCount = this.searchForm.get('children')?.value || 0;
    this.updateChildrenAges(initialChildrenCount);

    this.filteredLocations$ = merge(
      this.searchForm.get('location')!.valueChanges,
      this.locationFocus$
    ).pipe(
      switchMap(term => {
        if (typeof term === 'string' && term.length >= 1) {
          return this.searchLocations(term);
        }
        return of(this.quickSuggestions);
      })
    );
  }

  ngOnInit(): void {
    this.searchService.resetSearch();
    this.buildHolidays();
    this.buildWeekends();
    this.startTypingEffect();

    // Popüler öğeleri yükle
    if (isPlatformBrowser(this.platformId)) {
      // Sayfayı en üste scroll et
      window.scrollTo(0, 0);

      // Yan verileri geciktir - browser paralel istek limitini aşmamak için
      setTimeout(() => {
        this.loadPopularItems();
        this.loadBlogs();
      }, 300);
      
      // Router event'leri dinle - geri dönüşlerde yeniden yükle
      this.routerSubscription = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: any) => {
        if (event.url === '/' || event.url === '') {
          // Sayfayı en üste scroll et
          window.scrollTo(0, 0);
          this.loadPopularItems();
          // Animasyonları ve slider'ı yeniden başlat
          setTimeout(() => {
            this.initAnimations();
            this.cdr.detectChanges();
          }, 100);
        }
      });
      
      // İlk yüklemede animasyonları başlat
      setTimeout(() => this.initAnimations(), 100);

      // Promo slider
      this.promoTimer = setInterval(() => {
        this.activePromoSlide = this.activePromoSlide === 0 ? 1 : 0;
        this.cdr.detectChanges();
      }, 4000);

      // Bento kartları dönüşü (5 saniyede bir 1 kaydır)
      this.bentoRotateTimer = setInterval(() => {
        if (this.popularItems.length > 4) {
          this.bentoOffset = (this.bentoOffset + 1) % this.popularItems.length;
          this.cdr.detectChanges();
        }
      }, 5000);
    }
    
    // Giriş tarihi değiştiğinde çıkış tarihini kontrol et
    this.dateSubscription = this.searchForm.get('checkInDate')?.valueChanges.subscribe(checkInDate => {
      if (checkInDate) {
        const checkOutDate = this.searchForm.get('checkOutDate')?.value;
        if (checkOutDate && checkOutDate <= checkInDate) {
          const newCheckOut = new Date(checkInDate);
          newCheckOut.setDate(newCheckOut.getDate() + 1);
          this.searchForm.get('checkOutDate')?.setValue(newCheckOut);
        }
      }
    });
    
  }

  /**
   * Dropdown menüler dışına tıklandığında kapat
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    
    // Yaş dropdown'u dışına tıklandığında kapat
    if (!target.closest('.js-ages-dropdown')) {
      this.activeAgeDropdown = null;
    }
    
    // Takvim dışına tıklandığında kapat
    if (this.isCalendarOpen &&
        !target.closest('.search-field--date') &&
        !target.closest('app-date-range-picker') &&
        !target.closest('.desktop-calendar-panel') &&
        !target.closest('.mobile-calendar-overlay')) {
      this.isCalendarOpen = false;
    }
    
    // Misafir seçici dışına tıklandığında kapat
    if (this.isGuestSelectorOpen &&
        !target.closest('.search-field--guests') &&
        !target.closest('.searchMenu-guests__field') &&
        !target.closest('.mobile-guest-overlay')) {
      this.isGuestSelectorOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.stopTypingEffect();
    this.stopPopularAutoplay();
    if (this.promoTimer) { clearInterval(this.promoTimer); }
    if (this.bentoRotateTimer) { clearInterval(this.bentoRotateTimer); }
    if (this.dateSubscription) {
      this.dateSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Popüler öğeler hala boşsa tekrar yükle
      if (this.popularItems.length === 0) {
        this.loadPopularItems();
      }
    }
  }

  /**
   * Sayfa animasyonlarını başlatır
   */
  private initAnimations(): void {
    // data-anim-wrap elementlerini bul ve animasyonları tetikle
    const animWrapElements = document.querySelectorAll('[data-anim-wrap]');
    animWrapElements.forEach((wrap: any) => {
      wrap.classList.add('animated');
      
      // İçindeki data-anim-child elementlerini de tetikle
      const children = wrap.querySelectorAll('[data-anim-child]');
      children.forEach((child: any, index: number) => {
        setTimeout(() => {
          child.classList.add('is-in-view');
        }, index * 100);
      });
    });

    // data-anim elementlerini tetikle
    const animElements = document.querySelectorAll('[data-anim]');
    animElements.forEach((el: any, index: number) => {
      setTimeout(() => {
        el.classList.add('is-in-view');
      }, index * 50);
    });
  }

  /**
   * Swiper section slider'ını başlatır
   */
  private initSectionSlider(): void {
    const sliders = document.querySelectorAll('.js-section-slider:not(.swiper-initialized)');
    if (!sliders.length) return;

    sliders.forEach((el: any) => {
      try {
        const prevNavElement = el.querySelector('.js-prev');
        const nextNavElement = el.querySelector('.js-next');
        const scrollbarEl = el.querySelector('.js-scrollbar');

        const gap = parseInt(el.getAttribute('data-gap') || '30');
        const loop = el.hasAttribute('data-loop');
        const centered = el.hasAttribute('data-center');
        
        const scrollbar = scrollbarEl ? {
          el: scrollbarEl,
          draggable: true,
        } : false;

        const colsAttr = el.getAttribute('data-slider-cols');
        if (!colsAttr) return;
        
        const colsArray = colsAttr.split(' ');

        let cols_base = 2, cols_xl = 4, cols_lg = 3, cols_md = 2, cols_sm = 2;

        colsArray.forEach((col: string) => {
          const num = parseInt(col.replace(/\D/g, '')) || 1;
          if (col.includes('base')) cols_base = num;
          if (col.includes('xl')) cols_xl = num;
          if (col.includes('lg')) cols_lg = num;
          if (col.includes('md')) cols_md = num;
          if (col.includes('sm')) cols_sm = num;
        });

        if (typeof Swiper !== 'undefined') {
          new Swiper(el, {
            speed: 600,
            autoHeight: true,
            centeredSlides: centered,
            parallax: true,
            watchSlidesVisibility: true,
            loop: loop,
            loopAdditionalSlides: 1,
            preloadImages: false,
            lazy: true,
            scrollbar: scrollbar,
            spaceBetween: gap,
            slidesPerView: cols_base,
            grabCursor: true,
            navigation: {
              prevEl: prevNavElement,
              nextEl: nextNavElement,
            },
            breakpoints: {
              0: { slidesPerView: cols_base, spaceBetween: gap },
              575: { slidesPerView: cols_sm, spaceBetween: gap },
              768: { slidesPerView: cols_md, spaceBetween: gap },
              992: { slidesPerView: cols_lg, spaceBetween: gap },
              1200: { slidesPerView: cols_xl, spaceBetween: gap },
            }
          });
        }
      } catch (e) {
      }
    });
  }

  toggleMenu(): void {
    this.isMenuActive = !this.isMenuActive;
  }
  
  private initializeDates(): void {
    const checkIn = new Date();
    checkIn.setDate(checkIn.getDate() + 1);
    
    const checkOut = new Date();
    checkOut.setDate(checkOut.getDate() + 8);
    
    this.searchForm.patchValue({
      checkInDate: checkIn,
      checkOutDate: checkOut
    });
  }

  private searchLocations(term: string): Observable<AutocompleteResult[]> {
    const url = `${this.apiUrl}/Autocomplete/autocomplete`;
    const params = {
      term: term,
      lang: 'tr',
      limit: '15',
      type: 'all'
    };

    return this.http.get<AutocompleteResponse>(url, { params, withCredentials: true }).pipe(
      map(response => {
        return response.results || [];
      }),
      catchError(error => {
        this.error = 'Lokasyon araması yapılamadı. Lütfen tekrar deneyin.';
        return of([]);
      })
    );
  }

  onLocationFocus(): void {
    this.stopTypingEffect();
    this.typingPlaceholder = '';
    const current = this.searchForm.get('location')?.value || '';
    this.locationFocus$.next(current);
  }

  onLocationBlur(): void {
    const val = this.searchForm.get('location')?.value || '';
    if (!val.trim()) {
      this.startTypingEffect();
    }
  }

  onLocationSelect(location: AutocompleteResult): void {
    this.selectedLocation = location;
    this.searchForm.patchValue({
      location: location.label
    });
    this.isMobileLocationOpen = false;
    this.mobileLocationSearch = '';
  }

  openMobileLocation(): void {
    if (window.innerWidth <= 991) {
      this.isMobileLocationOpen = true;
      this.mobileLocationSearch = '';
      setTimeout(() => {
        const input = document.querySelector('.mobile-location-overlay__input') as HTMLInputElement;
        input?.focus();
      }, 100);
    }
  }

  closeMobileLocation(): void {
    this.isMobileLocationOpen = false;
    this.mobileLocationSearch = '';
  }

  onMobileLocationInput(term: string): void {
    this.mobileLocationSearch = term;
    this.searchForm.patchValue({ location: term });
  }
  
  toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
    if (this.isCalendarOpen) {
      this.isGuestSelectorOpen = false;
      this.mobileCalendarTab = 'calendar';
      this.autoTrigger?.closePanel();
    }
  }

  closeCalendar(): void {
    this.isCalendarOpen = false;
  }

  onDateRangeSelected(event: { startDate: Date; endDate: Date }): void {
    this.searchForm.patchValue({
      checkInDate: event.startDate,
      checkOutDate: event.endDate
    });
    this.isCalendarOpen = false;
  }
  
  toggleGuestSelector(): void {
    this.isGuestSelectorOpen = !this.isGuestSelectorOpen;
    if (this.isGuestSelectorOpen) {
      this.isCalendarOpen = false;
      this.autoTrigger?.closePanel();
    }
  }
  
  updateGuestCount(type: 'adults' | 'children' | 'rooms', increment: boolean): void {
    const control = this.searchForm.get(type);
    if (!control) return;

    const currentValue = control.value;
    const newValue = increment ? currentValue + 1 : currentValue - 1;
    
    let min, max;
    
    if (type === 'adults') {
      min = 1;
      max = 10;
    } else if (type === 'children') {
      min = 0;
      max = 6;
    } else if (type === 'rooms') {
      min = 1;
      max = 2; // maksimum 2 oda
    }

    if (min !== undefined && max !== undefined && newValue >= min && newValue <= max) {
      control.setValue(newValue);
      
      // Çocuk sayısı değiştiğinde yaş array'ini güncelle
      if (type === 'children') {
        this.updateChildrenAges(newValue);
      }

      // Oda sayısı 2 olduğunda 2. oda değerlerini initialize et, 1'e düşerse sıfırla
      if (type === 'rooms') {
        if (newValue === 2) {
          if ((this.searchForm.get('room2Adults')?.value ?? 0) < 1) {
            this.searchForm.get('room2Adults')?.setValue(1);
          }
          if ((this.searchForm.get('room2Children')?.value ?? -1) < 0) {
            this.searchForm.get('room2Children')?.setValue(0);
          }
        } else if (newValue === 1) {
          this.searchForm.get('room2Adults')?.setValue(1);
          this.searchForm.get('room2Children')?.setValue(0);
          this.room2ChildrenAges = [];
        }
      }
    }
  }

  updateChildrenAges(childrenCount: number): void {
    // Mevcut yaşları koru, eksik olanları ekle, fazla olanları sil
    if (childrenCount > this.childrenAges.length) {
      // Yeni çocuklar ekle (varsayılan yaş 5)
      for (let i = this.childrenAges.length; i < childrenCount; i++) {
        this.childrenAges.push(5);
      }
    } else if (childrenCount < this.childrenAges.length) {
      // Fazla çocukları sil
      this.childrenAges = this.childrenAges.slice(0, childrenCount);
    }
  }

  // 2. oda çocuk yaş yönetimi
  updateChildrenAgesRoom2(childrenCount: number): void {
    if (childrenCount > this.room2ChildrenAges.length) {
      for (let i = this.room2ChildrenAges.length; i < childrenCount; i++) {
        this.room2ChildrenAges.push(5);
      }
    } else if (childrenCount < this.room2ChildrenAges.length) {
      this.room2ChildrenAges = this.room2ChildrenAges.slice(0, childrenCount);
    }
  }

  updateChildAgeRoom2(index: number, age: number): void {
    if (index >= 0 && index < this.room2ChildrenAges.length) {
      this.room2ChildrenAges[index] = age;
      this.room2ChildrenAges = [...this.room2ChildrenAges]; // Yeni referans oluştur
    }
  }

  onChildAgeChangeRoom2(event: Event, index: number): void {
    const target = event.target as HTMLSelectElement;
    const age = parseInt(target.value, 10);
    this.updateChildAgeRoom2(index, age);
  }

  updateGuestCountRoom2(type: 'room2Adults' | 'room2Children', increment: boolean): void {
    const control = this.searchForm.get(type);
    if (!control) return;

    const currentValue = control.value;
    const newValue = increment ? currentValue + 1 : currentValue - 1;

    let min = type === 'room2Adults' ? 1 : 0;
    let max = type === 'room2Adults' ? 10 : 6;

    if (newValue >= min && newValue <= max) {
      control.setValue(newValue);
      if (type === 'room2Children') {
        this.updateChildrenAgesRoom2(newValue);
      }
    }
  }

  addRoom(): void {
    const current = this.searchForm.get('rooms')?.value ?? 1;
    if (current < 2) {
      this.updateGuestCount('rooms', true);
    }
  }

  removeRoom(): void {
    // 2. odayı kaldır ve varsayılan değerlere sıfırla
    this.searchForm.get('rooms')?.setValue(1);
    this.searchForm.get('room2Adults')?.setValue(1);
    this.searchForm.get('room2Children')?.setValue(0);
    this.room2ChildrenAges = [];
  }

  getTotalAdults(): number {
    const room1 = this.searchForm.get('adults')?.value ?? 0;
    const rooms = this.searchForm.get('rooms')?.value ?? 1;
    if (rooms === 2) {
      const room2 = this.searchForm.get('room2Adults')?.value ?? 0;
      return room1 + room2;
    }
    return room1;
  }

  getTotalChildren(): number {
    const room1 = this.searchForm.get('children')?.value ?? 0;
    const rooms = this.searchForm.get('rooms')?.value ?? 1;
    if (rooms === 2) {
      const room2 = this.searchForm.get('room2Children')?.value ?? 0;
      return room1 + room2;
    }
    return room1;
  }

  updateChildAge(index: number, age: number): void {
    if (index >= 0 && index < this.childrenAges.length) {
      this.childrenAges[index] = age;
      this.childrenAges = [...this.childrenAges]; // Yeni referans oluştur - change detection için
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  onChildAgeChange(event: Event, index: number): void {
    const target = event.target as HTMLSelectElement;
    const age = parseInt(target.value, 10);
    this.updateChildAge(index, age);
  }

  toggleAgeDropdown(index: number): void {
    this.activeAgeDropdown = this.activeAgeDropdown === index ? null : index;
  }

  selectChildAge(index: number, age: number): void {
    this.updateChildAge(index, age);
    this.activeAgeDropdown = null; // Dropdown'u kapat
  }

  private getLocationType(type: string): number {
    switch (type.toLowerCase()) {
      case 'city':
        return 0;
      case 'country':
        return 1;
      case 'continent':
        return 2;
      case 'district':
        return 3;
      case 'area':
        return 4;
      case 'hotel':
      case 'cyprus': // Kıbrıs otelleri de Hotel tipi olarak işlenir
        return 5;
      case 'category':
        return 6;
      default:
        return 0;
    }
  }

  private createRooms(): { passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] }[] {
    const rooms: { passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] }[] = [];
    const numberOfRooms = this.searchForm.get('rooms')?.value ?? 1;
    const adultsRoom1 = this.searchForm.get('adults')?.value ?? 0;
    const childrenRoom1 = this.searchForm.get('children')?.value ?? 0;
    const adultsRoom2 = numberOfRooms === 2 ? (this.searchForm.get('room2Adults')?.value ?? 0) : 0;
    const childrenRoom2 = numberOfRooms === 2 ? (this.searchForm.get('room2Children')?.value ?? 0) : 0;

    // 1. Oda
    const room1Passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] = [];
    
    // 1. oda yetişkinler
    for (let i = 0; i < adultsRoom1; i++) {
      room1Passengers.push({
        age: 0,
        birthday: null,
        gender: null,
        passengerType: 0,
      });
    }

    // 1. oda çocukları
    for (let i = 0; i < childrenRoom1; i++) {
      const childAge = this.childrenAges[i] || 5;
      room1Passengers.push({
        age: childAge,
        birthday: null,
        gender: null,
        passengerType: 1,
      });
    }

    rooms.push({ passengers: room1Passengers });

    // 2. Oda (varsa)
    if (numberOfRooms === 2) {
      const room2Passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] = [];
      
      // 2. oda yetişkinler
      for (let i = 0; i < adultsRoom2; i++) {
        room2Passengers.push({
          age: 0,
          birthday: null,
          gender: null,
          passengerType: 0,
        });
      }

      // 2. oda çocukları
      for (let i = 0; i < childrenRoom2; i++) {
        const childAge = this.room2ChildrenAges[i] || 5;
        room2Passengers.push({
          age: childAge,
          birthday: null,
          gender: null,
          passengerType: 1,
        });
      }

      rooms.push({ passengers: room2Passengers });
    }

    return rooms;
  }

  performSearch(): void {
    if (this.loading) return; // Zaten arama devam ediyor
    if (!this.selectedLocation) {
      this.error = 'Lütfen bir lokasyon seçin.';
      return;
    }
    this.loading = true;
    this.error = null;

    // Yeni arama için SearchService'i sıfırla
    this.searchService.resetSearch();

    let parentId = 0;
    if (this.selectedLocation.parentId) {
      const parsedParentId = parseInt(this.selectedLocation.parentId, 10);
      if (!isNaN(parsedParentId)) {
        parentId = parsedParentId;
      }
    }

    const locationType = this.getLocationType(this.selectedLocation.type);
    const locationId = parseInt(this.selectedLocation.value?.toString() || this.selectedLocation.id?.toString() || '0', 10);

    // Kıbrıs oteli veya Hotel tipinde ise özel işlem
    const isCyprusOrHotel = this.selectedLocation.type?.toLowerCase() === 'cyprus' ||
                            this.selectedLocation.type?.toLowerCase() === 'hotel';

    // Kıbrıs oteli için masterHotelId varsa onu kullan, yoksa id'yi kullan
    const hotelIdValue = isCyprusOrHotel
      ? parseInt(this.selectedLocation.masterHotelId?.toString() || this.selectedLocation.id?.toString() || '0', 10)
      : 0;

    const destination = {
      destinationId: isCyprusOrHotel ? 0 : locationId,
      hotelId: isCyprusOrHotel ? hotelIdValue : 0,
      destinationLabel: this.selectedLocation.label,
      destinationParentId: isCyprusOrHotel ? 0 : parentId, // Kıbrıs için parentId 0
      locationType: isCyprusOrHotel ? LocationType.Hotel : locationType, // Kıbrıs için locationType 5 (Hotel)
    };

    const hasCheckIn = this.searchForm.get('checkInDate')?.value;
    const hasCheckOut = this.searchForm.get('checkOutDate')?.value;
    const hasDates = hasCheckIn && hasCheckOut;

    // Kullanıcı arama kriterlerini ÖNCE kaydet (orijinal misafir sayılarıyla)
    const userCriteria: UserSearchCriteria = {
      location: this.searchForm.get('location')?.value,
      selectedLocation: this.selectedLocation,
      checkInDate: hasCheckIn,
      checkOutDate: hasCheckOut,
      adults: this.searchForm.get('adults')?.value,
      children: this.searchForm.get('children')?.value,
      rooms: this.searchForm.get('rooms')?.value,
      room2Adults: this.searchForm.get('room2Adults')?.value,
      room2Children: this.searchForm.get('room2Children')?.value,
      childrenAges: [...this.childrenAges],
      room2ChildrenAges: [...this.room2ChildrenAges]
    };
    this.searchService.setUserSearchCriteria(userCriteria);

    const searchRequest: SearchRequest = {
      currency: 'TRY',
      countryCode: 'TR',
      destinations: [destination],
      pageNumber: 1,
      pageSize: 10,
      rooms: !hasDates
        ? [{ passengers: [{ age: 0, birthday: null, gender: null, passengerType: 0 }] }]
        : this.createRooms(),
      includeRoomPrices: false,
    };

    if (hasDates) {
      const checkInDate = new Date(hasCheckIn);
      checkInDate.setHours(0, 0, 0, 0);
      const checkOutDate = new Date(hasCheckOut);
      checkOutDate.setHours(0, 0, 0, 0);
      searchRequest.checkInDate = toLocalISOString(checkInDate);
      searchRequest.checkOutDate = toLocalISOString(checkOutDate);
    } else {
      searchRequest.checkInDate = null as any;
      searchRequest.checkOutDate = null as any;
    }

    // Hemen hotel-list'e navigate et, arama arka planda devam etsin
    this.router.navigate(['/hotel-list'], hasDates ? {} : { queryParams: { from: 'nav' } });

    // SearchService üzerinden arama yap - response service içinde BehaviorSubject'e yazılır
    // Component destroy olsa bile service yaşamaya devam eder
    this.searchService.searchHotels(searchRequest).subscribe({
      next: (data: SearchResponse) => {
        this.searchResults = data;
        this.loading = false;
      },
      error: (error) => {
        this.error = 'API bağlantısı kurulamadı. Lütfen tekrar deneyin.';
        this.loading = false;
      }
    });
  }

  onSearchResults(results: SearchResponse): void {
    this.searchResults = results;
  }

  onSearchError(error: string): void {
    this.error = error;
  }

  /**
   * ⭐ Popüler öğeye tıklandığında arama yapar
   * Type'a göre farklı davranış sergiler: Destination, Hotel, Category
   */
  searchByPopularItem(item: PopularItem): void {
    if (this.loading) return; // Zaten arama devam ediyor
    this.loading = true;
    this.error = null;

    // Yeni arama için SearchService'i sıfırla
    this.searchService.resetSearch();

    // Type'a göre destination yapılandırması
    let destinationId = 0;
    let hotelId = 0;
    let locationType = item.locationType ?? 0;
    let parentId = item.parentId ?? 0;

    switch (item.type) {
      case 'Destination':
        destinationId = item.referenceId;
        locationType = item.locationType ?? 0;
        break;
      case 'Hotel':
        hotelId = item.referenceId;
        locationType = LocationType.Hotel;
        break;
      case 'Category':
        // Kategori için özel işlem - category ID'yi kullan
        destinationId = item.referenceId;
        locationType = LocationType.Category;
        break;
    }

    const searchRequest: SearchRequest = {
      currency: 'TRY',
      countryCode: 'TR',
      destinations: [{
        destinationId: destinationId,
        destinationLabel: item.label,
        destinationParentId: parentId,
        locationType: locationType,
        hotelId: hotelId
      }],
      pageNumber: 1,
      pageSize: 10,
      checkInDate: null as any,
      checkOutDate: null as any,
      rooms: [{
        passengers: [
          { age: 0, birthday: null, gender: null, passengerType: 0 }
        ]
      }],
      includeRoomPrices: false,
    };

    // Kullanıcı arama kriterlerini kaydet
    const userCriteria: UserSearchCriteria = {
      location: item.label,
      selectedLocation: {
        id: item.referenceId,
        label: item.label,
        value: item.referenceId,
        parentId: parentId.toString(),
        type: item.type.toLowerCase()
      },
      checkInDate: null as any, // Tarihsiz arama
      checkOutDate: null as any, // Tarihsiz arama
      adults: 1, // Tarihsiz aramalarda sadece 1 yolcu
      children: 0,
      rooms: 1,
      room2Adults: 1,
      room2Children: 0,
      childrenAges: [],
      room2ChildrenAges: []
    };
    this.searchService.setUserSearchCriteria(userCriteria);

    // Hemen hotel-list'e navigate et
    this.router.navigate(['/hotel-list'], { queryParams: { from: 'nav' } });

    // Arama arka planda devam etsin - response service içinde BehaviorSubject'e yazılır
    this.searchService.searchHotels(searchRequest).subscribe({
      next: () => {
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  /**
   * Popüler öğeleri API'den yükler
   */
  loadPopularItems(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Service'de veri varsa direkt kullan
    const cached = this.popularService.cachedItems;
    if (cached && cached.length > 0) {
      this.popularItems = [...cached];
      this.activePopularIndex = Math.floor(cached.length / 2);
      this.buildQuickSuggestions();
      this.popularLoading = false;
      this.cdr.detectChanges();
      this.startPopularAutoplay();
      return;
    }

    // Veri yoksa API'den çek
    this.popularLoading = true;
    this.cdr.detectChanges();

    this.popularService.getActivePopularItems().subscribe({
      next: (items) => {
        if (items && items.length > 0) {
          this.popularItems = [...items];
          this.activePopularIndex = Math.floor(items.length / 2);
          this.buildQuickSuggestions();
          this.startPopularAutoplay();
        }
        this.popularLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.popularLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private buildQuickSuggestions(): void {
    this.quickSuggestions = this.popularItems.map(item => ({
      id: item.referenceId,
      label: item.label,
      value: item.referenceId,
      parentId: (item.parentId || 0).toString(),
      type: item.type.toLowerCase()
    }));
    this.buildMobileCarouselSlides();
  }

  private buildMobileCarouselSlides(): void {
    this.mobileCarouselSlides = [];
    const items = this.popularItems;
    // Popüler öğelerin arasına promo kartları serpiştir
    for (let i = 0; i < items.length; i++) {
      this.mobileCarouselSlides.push({ type: 'destination', item: items[i] });
      // 2. öğeden sonra Maximum, 4. öğeden sonra Tur Hediye
      if (i === 1) {
        this.mobileCarouselSlides.push({ type: 'promo', promoId: 'maximum' });
      }
      if (i === 3) {
        this.mobileCarouselSlides.push({ type: 'promo', promoId: 'tour' });
      }
    }
  }

  private touchStartX = 0;

  private popularAutoplayTimer: any = null;

  private get carouselLength(): number {
    return this.mobileCarouselSlides.length || this.popularItems.length;
  }

  scrollPopular(direction: number): void {
    const len = this.carouselLength;
    if (!len) return;
    this.activePopularIndex = (this.activePopularIndex + direction + len) % len;
  }

  goToPopular(index: number): void {
    const len = this.carouselLength;
    this.activePopularIndex = ((index % len) + len) % len;
    this.restartAutoplay();
  }

  getVisiblePopularItems(): any[] {
    const len = this.popularItems.length;
    if (len <= 4) return this.popularItems;
    const result = [];
    for (let i = 0; i < 4; i++) {
      result.push(this.popularItems[(this.bentoOffset + i) % len]);
    }
    return result;
  }

  getPopularCardClass(i: number): string {
    const len = this.mobileCarouselSlides.length || this.popularItems.length;
    if (!len) return '';
    const active = this.activePopularIndex;
    if (i === active) return 'popular-card--active';

    const diff = ((i - active) % len + len) % len;
    if (diff === 1) return 'popular-card--next';
    if (diff === len - 1) return 'popular-card--prev';
    if (diff <= len / 2) return 'popular-card--far-next';
    return 'popular-card--far-prev';
  }

  onPopularCardClick(item: any, index: number): void {
    if (index === this.activePopularIndex) {
      if (item) this.searchByPopularItem(item);
    } else {
      const len = this.carouselLength;
      this.activePopularIndex = ((index % len) + len) % len;
      this.restartAutoplay();
    }
  }

  startPopularAutoplay(): void {
    this.stopPopularAutoplay();
    this.popularAutoplayTimer = setInterval(() => {
      this.scrollPopular(1);
      this.cdr.detectChanges();
    }, 3500);
  }

  stopPopularAutoplay(): void {
    if (this.popularAutoplayTimer) {
      clearInterval(this.popularAutoplayTimer);
      this.popularAutoplayTimer = null;
    }
  }

  restartAutoplay(): void {
    this.stopPopularAutoplay();
    this.startPopularAutoplay();
  }

  @HostListener('document:touchstart', ['$event'])
  onTouchStart(e: TouchEvent): void {
    if (!this.popularTrack?.nativeElement) return;
    const track = this.popularTrack.nativeElement as HTMLElement;
    if (track.contains(e.target as Node)) {
      this.touchStartX = e.touches[0].clientX;
      this.stopPopularAutoplay();
    }
  }

  @HostListener('document:touchend', ['$event'])
  onTouchEnd(e: TouchEvent): void {
    if (!this.popularTrack?.nativeElement || !this.touchStartX) return;
    const track = this.popularTrack.nativeElement as HTMLElement;
    if (track.contains(e.target as Node)) {
      const diff = this.touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        this.scrollPopular(diff > 0 ? 1 : -1);
      }
      this.touchStartX = 0;
      this.startPopularAutoplay();
    }
  }

  /**
   * Popüler öğe resim URL'ini döndürür
   */
  getPopularItemImage(item: PopularItem): string {
    return this.popularService.getImageUrl(item.imagePath);
  }

  onSearch(): void {
    this.loading = true;
    this.searchService.searchHotels(this.searchParams).subscribe(
      (response) => {
        this.loading = false;
        this.searchService.handleSearchResponse(response);
      },
      (error) => {
        this.loading = false;
      }
    );
  }

  // Blog metodları
  loadBlogs(): void {
    this.blogLoading = true;
    this.blogService.getPublishedBlogs().subscribe({
      next: (blogs) => {
        this.blogs = blogs.slice(0, 3);
        this.blogLoading = false;
      },
      error: () => {
        this.blogLoading = false;
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('tr-TR', options);
  }

  getBlogImage(blog: Blog): string {
    const coverImageUrl = this.blogService.getImageUrl(blog.coverImage);
    if (coverImageUrl) return coverImageUrl;
    return 'assets/img/blog/blog.jpg';
  }

  getTruncatedTitle(title: string, wordLimit: number = 8): string {
    if (!title) return '';
    const words = title.split(' ');
    if (words.length <= wordLimit) return title;
    return words.slice(0, wordLimit).join(' ') + '...';
  }

  hotelQuickLinks = [
    { destinationId: 38140, label: 'Antalya Otelleri', parentId: 2369, locationType: 0 },
    { destinationId: 1128, label: 'Akdeniz Otelleri', parentId: 0, locationType: 6 },
    { destinationId: 1129, label: 'Ege Otelleri', parentId: 0, locationType: 6 },
    { destinationId: 1291, label: 'Herşey Dahil', parentId: 0, locationType: 6 },
    { destinationId: 1276, label: 'Erken Rezervasyon', parentId: 0, locationType: 6 },
    { destinationId: 1173, label: 'Kıbrıs Otelleri', parentId: 0, locationType: 6 },
    { destinationId: 1172, label: 'Kıbrıs Her Şey Dahil', parentId: 0, locationType: 6 }
  ];

  searchByQuickLink(item: { destinationId: number; label: string; parentId: number; locationType: number }): void {
    // Lokasyonu forma yerleştir
    this.searchForm.patchValue({ location: item.label });
    this.selectedLocation = {
      id: item.destinationId,
      label: item.label,
      value: item.destinationId,
      parentId: item.parentId.toString(),
      type: item.locationType === 6 ? 'category' : 'destination'
    };
    // Autocomplete paneli kapat
    this.autoTrigger?.closePanel();

    // Search formuna yumuşak kaydır
    const searchEl = document.querySelector('.mainSearch');
    if (searchEl) {
      searchEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Takvimi otomatik aç (scroll tamamlandıktan sonra)
    setTimeout(() => {
      this.isGuestSelectorOpen = false;
      this.isCalendarOpen = true;
      this.mobileCalendarTab = 'calendar';
    }, 450);
  }
}